import { Component, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { FormControl } from '@angular/forms';
import {
	takeUntil,
	debounceTime,
	distinctUntilChanged,
	delay,
	filter,
} from 'rxjs/operators';
import { MatSort } from '@angular/material/sort';
import { animateList } from 'src/shared/animations';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import { UserListArgs } from 'src/shared/api';
import { customersFragments } from 'src/admin-app/customers/customers.routes';
import * as CustomerUsersSelectors from '../+state/customer-users.selectors';
import * as CustomerUsersActions from '../+state/customer-users.actions';
import { select, Store } from '@ngrx/store';
import { CustomerUsersPartialState } from '../+state/customer-users.reducer';
import { FilterCollection } from 'src/admin-app/utilities/list-filtering/shared';
import {
	withAbstractControl,
	withMatPaginator,
	withMatSort,
} from 'src/admin-app/utilities/list-filtering';
import { usersFragments } from '../../users.routes';

@Component({
	templateUrl: './registration-request-list.component.html',
	animations: [animateList],
})
export class RegistrationRequestListComponent extends SelfDestruct
	implements AfterViewInit, OnDestroy {
	@ViewChild(MatSort, { static: true }) sort: MatSort;
	@ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

	readonly emptyColumns = ['empty'];
	readonly resultColumns = [
		'name',
		'email',
		'company',
		'phoneNumber',
		'approve',
	];
	readonly customersFragments = customersFragments;
	readonly usersFragments = usersFragments;

	readonly filter = new FormControl('') as FormControl;

	readonly isLoading$ = this.store.pipe(
		select(CustomerUsersSelectors.getRegistrationRequestListLoadingStatus)
	);
	readonly listMessage$ = this.store.pipe(
		select(CustomerUsersSelectors.getRegistrationRequestListMessage)
	);

	readonly pagedRegistrationRequests$ = this.store.pipe(
		select(CustomerUsersSelectors.getFilteredAndPagedRegistrationRequests)
	);

	readonly pagedRegistrationRequestsCount$ = this.store.pipe(
		select(CustomerUsersSelectors.getPagedRegistrationRequestsCount)
	);

	constructor(private readonly store: Store<CustomerUsersPartialState>) {
		super();
	}

	ngAfterViewInit() {
		const filters = new FilterCollection<UserListArgs>(
			withAbstractControl(this.filter),
			withMatPaginator(this.paginator),
			withMatSort(this.sort)
		);

		filters.valueChanges
			.pipe(
				debounceTime(200),
				distinctUntilChanged(),
				takeUntil(this.destroyed)
			)
			.subscribe((v) =>
				this.store.dispatch(
					CustomerUsersActions.filtersUpdated({ filters: v })
				)
			);

		// Initialize filters with URL value
		this.store
			.pipe(
				select(CustomerUsersSelectors.getListArgs),
				delay(0),
				filter<UserListArgs>(Boolean),
				takeUntil(this.destroyed)
			)
			.subscribe((f) => filters.setValue(f));
	}

	denyRequest(id: number) {
		this.store.dispatch(
			CustomerUsersActions.registrationRequestDenied({ id })
		);
	}

	ngOnDestroy() {
		this.store.dispatch(
			CustomerUsersActions.registrationRequestsListDestroyed()
		);
		super.ngOnDestroy();
	}
}
